import React, { JSX, useEffect, useMemo, useState } from "react";
import { ButtonStyle } from "components/buttons/button/button";
import { Modal, ModalAction } from "components/modal/modal";
import { t } from "utilities/localization/t";
import { ProviderFeeInvoiceRecord } from "models/view-models/providers/provider-fee-invoice-record";
import { ProviderFeeInvoiceForm } from "components/providers/provider-invoice-form/provider-invoice-form";
import { ProviderFeeInvoiceService } from "utilities/services/providers/provider-fee-invoice-service";
import { ToastManager } from "utilities/toast/toast-manager";
import "./provider-fee-invoice-modal.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface ProviderInvoiceFeeModalProps {
    onEditFormSave?: (providerInvoiceFee: ProviderFeeInvoiceRecord) => Promise<boolean>;
    providerFeeInvoiceToEdit?: ProviderFeeInvoiceRecord;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "edit-provider-fee-invoice-modal";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ProviderInvoiceFeeModal: React.FC<ProviderInvoiceFeeModalProps> = ({
    open,
    setOpen,
    providerFeeInvoiceToEdit,
    onEditFormSave,
}): JSX.Element => {

    const initialProviderInvoiceFee = useMemo(() => providerFeeInvoiceToEdit ?? new ProviderFeeInvoiceRecord(), [providerFeeInvoiceToEdit]);
    const [providerInvoiceFee, setProviderInvoiceFee] = useState<ProviderFeeInvoiceRecord>(initialProviderInvoiceFee);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setProviderInvoiceFee(initialProviderInvoiceFee);
    }, [initialProviderInvoiceFee]);

    const editMode = providerFeeInvoiceToEdit?.id != null && providerFeeInvoiceToEdit?.id > 0;
    const title = editMode ? t("editProviderFeeInvoice") : t("addProviderFeeInvoice");

    const modalActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalClose(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("saveChanges"),
            onClick: () => handleSave(),
            disabled: 
                isDirty === false ||
                providerInvoiceFee.invoiceAmount <= 0 ||
                providerInvoiceFee.invoiceTitle.trim() === "" ||
                providerInvoiceFee.invoiceDate == null,
            style: ButtonStyle.Primary,
        },
    ];

    const cancelConfirmationActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => setShowConfirmationModal(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("confirm"),
            onClick: () => {
                setOpen(false);
                setShowConfirmationModal(false);
                setIsDirty(false);
                setProviderInvoiceFee(initialProviderInvoiceFee);
            },
            style: ButtonStyle.Destructive,
        },
    ];

    const onModalClose = (): void => {
        if (isDirty) {
            setShowConfirmationModal(true);
        } else {
            setOpen(false);
        }
    };

    const { create: createProviderServiceFee } = ProviderFeeInvoiceService.useCreate();
    const { update: updateProviderServiceFee } = ProviderFeeInvoiceService.useUpdate();

    const handleSave = async () => {
        
        const response = editMode ? await updateProviderServiceFee(providerInvoiceFee) : await createProviderServiceFee(providerInvoiceFee);

        if (response) {
            await onEditFormSave?.(providerInvoiceFee);
            setIsDirty(false);
            setOpen(false);
            ToastManager.success(t("providerFeeInvoiceHasBeenUpdatedSuccessfully"));
        } else {
            // Inform user of failure.
            ToastManager.error(t("thereWasAnIssueUpdatingTheProviderFeeInvoice"));
        }
    };

    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            isOpen={open}
            onModalClose={onModalClose}
            title={title}
            actions={modalActionArray}
            modalStyle={""}>
            <div className={`${CSS_CLASS_NAME}__section`}>
                <ProviderFeeInvoiceForm
                    providerFeeInvoice={providerInvoiceFee}
                    onChange={setProviderInvoiceFee}
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                />
            </div>
            <Modal
                isOpen={showConfirmationModal}
                onModalClose={() => {}}
                actions={cancelConfirmationActions}
                modalStyle={"-inverted"}>
                {t("youHaveUnsavedChanges")}
                <br></br> {t("areYouSureYouWantToExit")}
            </Modal>
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { ProviderInvoiceFeeModal };

// #endregion Exports
